<template>
  <div class="flex flex-col bg-[#f8fafc] gap-4">
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-[#0d4169] flex flex-col pt-3">
      <div class="relative z-30 px-6 pb-3 flex items-center justify-between gap-4 border-b border-slate-100 bg-white">
        <h2 class="text-base font-bold text-[#0d4169] text-left m-0">HR</h2>
        <button
          type="button"
          class="px-4 py-2 bg-[#0d4169] text-white text-xs font-bold rounded-lg transition-all shadow-md shadow-[#0d4169]/10 hover:bg-[#0a3354] flex items-center gap-2 shrink-0"
          @click="openCategoryModal()"
        >
          + Category Criteria
        </button>
      </div>

      <div class="relative z-0 px-6 py-4">
        <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
          <div class="px-6 pt-2 pb-1">
            <div class="flex items-baseline gap-2 flex-wrap">
              <h2 class="text-base font-bold text-[#0d4169] text-left">
                Performance Criteria
              </h2>
            </div>
          </div>

          <form @submit.prevent="save" class="px-6 pb-5 pt-2">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto] gap-x-4 gap-y-2 items-end">
              <div class="flex flex-col gap-1">
                <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Category</label>
                <select
                  v-model="form.categoryId"
                  class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
                  required
                >
                  <option value="" disabled>
                    {{ categories.length === 0 ? 'Add a category first' : 'Select Category' }}
                  </option>
                  <option
                    v-for="cat in categories"
                    :key="cat.id"
                    :value="cat.id"
                  >
                    {{ cat.name }}
                  </option>
                </select>
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Criteria Name</label>
                <input
                  v-model.trim="form.name"
                  type="text"
                  placeholder="Criteria Name"
                  class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
                  required
                />
              </div>

              <div class="flex items-center gap-3 sm:col-span-2 lg:col-span-1">
                <button
                  type="submit"
                  :disabled="saving || (editingId != null ? !canEditPerformanceCriteria : !canAddPerformanceCriteria)"
                  class="min-w-[160px] px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div
                    v-if="saving"
                    class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
                  ></div>
                  <span v-if="!saving">{{ editingId != null ? 'Update' : 'Save' }}</span>
                </button>
                <button
                  v-if="editingId != null"
                  type="button"
                  :disabled="saving"
                  class="px-4 py-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors disabled:opacity-50"
                  @click="resetForm"
                >
                  Cancel
                </button>
              </div>
            </div>

            <p v-if="formError" class="mt-3 text-sm text-red-600 text-left">{{ formError }}</p>
          </form>

          <div class="px-6 pb-6">
            <p v-if="errorMessage" class="mb-3 text-sm text-red-600">{{ errorMessage }}</p>
            <div class="w-full overflow-x-auto">
              <table class="w-full min-w-[980px] border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Name</th>
                    <th colspan="5" class="text-center">Max Point</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="8" class="text-center py-10">
                      <div class="flex items-center justify-center">
                        <div class="h-8 w-8 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    </td>
                  </tr>
                  <template v-else v-for="(group, gIdx) in groupedRows" :key="group.category.id">
                    <tr class="category-row">
                      <td>{{ gIdx + 1 }}</td>
                      <td class="name-cell">{{ group.category.name }}</td>
                      <td>Low</td>
                      <td>Moderate</td>
                      <td>Medium</td>
                      <td>High</td>
                      <td>Very high</td>
                      <td class="actions">
                        <button type="button" @click="openCategoryModal(group.category)">
                          Edit
                        </button>
                        <label class="toggle-switch" title="Active">
                          <input
                            type="checkbox"
                            :checked="group.category.active"
                            :disabled="togglingCategoryId === group.category.id"
                            @change="toggleCategory(group.category)"
                          >
                          <span class="slider"></span>
                        </label>
                        <button type="button" class="delete-btn" @click="deleteCategory(group.category)">
                          Delete
                        </button>
                      </td>
                    </tr>
                    <tr
                      v-for="(row, rIdx) in group.criteria"
                      :key="row.id"
                      class="hover:bg-slate-100/50 transition-colors border-b"
                    >
                      <td>{{ gIdx + 1 }}.{{ rIdx + 1 }}</td>
                      <td class="name-cell">{{ row.name }}</td>
                      <td>{{ formatPoint(row.lowPoint) }}</td>
                      <td>{{ formatPoint(row.moderatePoint) }}</td>
                      <td>{{ formatPoint(row.mediumPoint) }}</td>
                      <td>{{ formatPoint(row.highPoint) }}</td>
                      <td>{{ formatPoint(row.veryHighPoint) }}</td>
                      <td class="actions">
                        <button type="button" @click="editEntry(row)">
                          Edit
                        </button>
                        <label class="toggle-switch" title="Active">
                          <input
                            type="checkbox"
                            :checked="row.active"
                            :disabled="togglingCriteriaId === row.id"
                            @change="toggleActive(row)"
                          >
                          <span class="slider"></span>
                        </label>
                        <button type="button" class="delete-btn" @click="deleteEntry(row)">
                          Delete
                        </button>
                      </td>
                    </tr>
                  </template>
                  <tr v-if="!loading && categories.length === 0 && rows.length === 0">
                    <td colspan="8" style="text-align:center; color:#94a3b8; padding: 32px 15px; font-style: italic;">
                      No performance criteria saved.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="isCategoryModalOpen"
    class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
    @click.self="closeCategoryModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-[20rem] flex flex-col transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl shrink-0">
        <h2 class="text-base font-bold m-0">
          {{ editingCategoryId != null ? 'Edit Category Criteria' : 'Add New Category Criteria' }}
        </h2>
        <button type="button" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white" @click="closeCategoryModal">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <form class="p-6 flex-1 flex flex-col" @submit.prevent="saveCategory">
        <div class="grid grid-cols-1 gap-y-4">
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Category Criteria Name</label>
            <input
              v-model.trim="categoryForm.name"
              type="text"
              placeholder="Enter category criteria name"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
              required
            />
          </div>
        </div>

        <p v-if="categoryError" class="mt-3 text-sm text-red-600">{{ categoryError }}</p>

        <div class="mt-auto pt-5 border-t border-slate-100 flex items-center justify-end gap-3">
          <button type="button" class="px-5 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors" @click="closeCategoryModal">
            Cancel
          </button>
          <button
            type="submit"
            :disabled="savingCategory || (editingCategoryId != null ? !canEditPerformanceCriteria : !canAddPerformanceCriteria)"
            class="min-w-[100px] px-8 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div
              v-if="savingCategory"
              class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
            ></div>
            <span v-if="!savingCategory">{{ editingCategoryId != null ? 'Update' : 'Save' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import { apiFetch } from '../../../utils/api'

const API_BASE = '/hr/performance-criteria'
const CATEGORY_API = `${API_BASE}/categories`
const PRIVILEGE_NAME = 'HR Performance Criteria'

const store = useStore()
const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canAddPerformanceCriteria = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(PRIVILEGE_NAME), 'Add')
})

const canEditPerformanceCriteria = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(PRIVILEGE_NAME), 'Edit')
})

const DEFAULT_POINTS = {
  lowPoint: 1,
  moderatePoint: 2,
  mediumPoint: 3,
  highPoint: 4,
  veryHighPoint: 5
}

const form = reactive({
  categoryId: '',
  name: '',
  ...DEFAULT_POINTS
})

const categoryForm = reactive({
  name: ''
})

const categories = ref([])
const rows = ref([])
const loading = ref(false)
const saving = ref(false)
const savingCategory = ref(false)
const errorMessage = ref('')
const formError = ref('')
const categoryError = ref('')
const editingId = ref(null)
const editingCategoryId = ref(null)
const isCategoryModalOpen = ref(false)
const togglingCategoryId = ref(null)
const togglingCriteriaId = ref(null)

const groupedRows = computed(() => {
  const sortedCategories = [...categories.value].sort((a, b) => {
    if (!!a.active !== !!b.active) return a.active ? -1 : 1
    return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
  })

  return sortedCategories.map((category) => {
    const criteria = rows.value
      .filter((row) => Number(row.categoryId) === Number(category.id))
      .sort((a, b) => {
        if (!!a.active !== !!b.active) return a.active ? -1 : 1
        return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
      })

    return { category, criteria }
  })
})

function formatPoint(value) {
  const num = Number(value)
  if (Number.isNaN(num)) return '-'
  return num.toFixed(1)
}

function isFormValid() {
  if (!form.categoryId || !form.name) return false
  return true
}

function applyDefaultPoints() {
  form.lowPoint = DEFAULT_POINTS.lowPoint
  form.moderatePoint = DEFAULT_POINTS.moderatePoint
  form.mediumPoint = DEFAULT_POINTS.mediumPoint
  form.highPoint = DEFAULT_POINTS.highPoint
  form.veryHighPoint = DEFAULT_POINTS.veryHighPoint
}

function resetForm() {
  form.categoryId = ''
  form.name = ''
  applyDefaultPoints()
  editingId.value = null
  formError.value = ''
}

async function fetchAll() {
  loading.value = true
  errorMessage.value = ''
  try {
    const [catRes, criteriaRes] = await Promise.all([
      apiFetch(CATEGORY_API),
      apiFetch(API_BASE),
    ])

    if (!catRes.ok) {
      const err = await catRes.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to load categories')
    }
    if (!criteriaRes.ok) {
      const err = await criteriaRes.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to load performance criteria')
    }

    const catData = await catRes.json().catch(() => [])
    const criteriaData = await criteriaRes.json().catch(() => [])
    categories.value = Array.isArray(catData) ? catData : []
    rows.value = Array.isArray(criteriaData) ? criteriaData : []
  } catch (err) {
    console.error('Error loading performance criteria:', err)
    categories.value = []
    rows.value = []
    errorMessage.value = err.message || 'Failed to load performance criteria'
  } finally {
    loading.value = false
  }
}

async function save() {
  if (saving.value) return
  if (editingId.value != null) {
    if (!canEditPerformanceCriteria.value) return
  } else if (!canAddPerformanceCriteria.value) {
    return
  }
  formError.value = ''
  if (!isFormValid()) {
    formError.value = 'Please fill in all required fields.'
    return
  }

  applyDefaultPoints()
  const payload = {
    categoryId: Number(form.categoryId),
    name: form.name,
    lowPoint: DEFAULT_POINTS.lowPoint,
    moderatePoint: DEFAULT_POINTS.moderatePoint,
    mediumPoint: DEFAULT_POINTS.mediumPoint,
    highPoint: DEFAULT_POINTS.highPoint,
    veryHighPoint: DEFAULT_POINTS.veryHighPoint
  }

  saving.value = true
  try {
    const isEdit = editingId.value != null
    const res = await apiFetch(
      isEdit ? `${API_BASE}/${editingId.value}` : API_BASE,
      {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    )
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.message || (isEdit ? 'Failed to update criteria' : 'Failed to save criteria'))
    }
    resetForm()
    await fetchAll()
  } catch (err) {
    console.error('Error saving performance criteria:', err)
    formError.value = err.message || 'Failed to save criteria'
  } finally {
    saving.value = false
  }
}

function editEntry(row) {
  editingId.value = row.id
  form.categoryId = row.categoryId
  form.name = row.name
  applyDefaultPoints()
  formError.value = ''
}

async function toggleActive(row) {
  if (!row?.id || togglingCriteriaId.value) return
  togglingCriteriaId.value = row.id
  try {
    const res = await apiFetch(`${API_BASE}/${row.id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: !row.active }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.message || 'Failed to update status')
    const idx = rows.value.findIndex((r) => r.id === row.id)
    if (idx >= 0) rows.value[idx] = { ...rows.value[idx], ...data }
    else await fetchAll()
  } catch (err) {
    console.error('Error toggling criteria status:', err)
    window.alert(err.message || 'Failed to update status')
  } finally {
    togglingCriteriaId.value = null
  }
}

async function deleteEntry(row) {
  if (!window.confirm(`Are you sure you want to delete "${row.name}"?`)) return
  try {
    const res = await apiFetch(`${API_BASE}/${row.id}`, { method: 'DELETE' })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.message || 'Failed to delete criteria')
    if (editingId.value === row.id) resetForm()
    await fetchAll()
  } catch (err) {
    console.error('Error deleting criteria:', err)
    window.alert(err.message || 'Failed to delete criteria')
  }
}

function openCategoryModal(category = null) {
  categoryError.value = ''
  if (category) {
    editingCategoryId.value = category.id
    categoryForm.name = category.name
  } else {
    editingCategoryId.value = null
    categoryForm.name = ''
  }
  isCategoryModalOpen.value = true
}

function closeCategoryModal() {
  if (savingCategory.value) return
  isCategoryModalOpen.value = false
  editingCategoryId.value = null
  categoryForm.name = ''
  categoryError.value = ''
}

async function saveCategory() {
  if (savingCategory.value) return
  if (editingCategoryId.value != null) {
    if (!canEditPerformanceCriteria.value) return
  } else if (!canAddPerformanceCriteria.value) {
    return
  }
  categoryError.value = ''
  if (!categoryForm.name) {
    categoryError.value = 'Category Criteria Name is required.'
    return
  }

  savingCategory.value = true
  try {
    const isEdit = editingCategoryId.value != null
    const res = await apiFetch(
      isEdit ? `${CATEGORY_API}/${editingCategoryId.value}` : CATEGORY_API,
      {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: categoryForm.name }),
      }
    )
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.message || (isEdit ? 'Failed to update category' : 'Failed to save category'))
    }

    const previousCategoryId = form.categoryId
  
    await fetchAll()
    if (!isEdit && data?.id && !previousCategoryId) {
      form.categoryId = data.id
    }
  } catch (err) {
    console.error('Error saving category:', err)
    categoryError.value = err.message || 'Failed to save category'
  } finally {
    savingCategory.value = false
    closeCategoryModal()
  }
}

async function toggleCategory(category) {
  if (!category?.id || togglingCategoryId.value) return
  togglingCategoryId.value = category.id
  try {
    const res = await apiFetch(`${CATEGORY_API}/${category.id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: !category.active }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.message || 'Failed to update category status')
    const idx = categories.value.findIndex((c) => c.id === category.id)
    if (idx >= 0) categories.value[idx] = { ...categories.value[idx], ...data }
    else await fetchAll()
  } catch (err) {
    console.error('Error toggling category status:', err)
    window.alert(err.message || 'Failed to update category status')
  } finally {
    togglingCategoryId.value = null
  }
}

async function deleteCategory(category) {
  if (!window.confirm(`Are you sure you want to delete category "${category.name}" and its criteria?`)) return
  try {
    const res = await apiFetch(`${CATEGORY_API}/${category.id}`, { method: 'DELETE' })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.message || 'Failed to delete category')
    if (Number(form.categoryId) === Number(category.id)) form.categoryId = ''
    if (editingId.value != null) {
      const stillExists = rows.value.some(
        (r) => r.id === editingId.value && Number(r.categoryId) !== Number(category.id)
      )
      if (!stillExists) resetForm()
    }
    await fetchAll()
  } catch (err) {
    console.error('Error deleting category:', err)
    window.alert(err.message || 'Failed to delete category')
  }
}

onMounted(() => {
  fetchAll()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: #1e293b;
}

th,
td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  font-size: 0.7rem;
}

th {
  background: #f1f5f9;
  font-weight: 600;
  cursor: default;
}

.name-cell {
  white-space: normal;
  min-width: 220px;
  max-width: 420px;
}

.category-row {
  background: #0d4169;
  color: #ffffff;
  font-weight: 600;
}

.category-row td {
  color: #ffffff;
  border-bottom: 1px solid #93c5fd;
}

tbody tr:hover {
  background: #f1f5f9;
}

.category-row:hover {
  background: #0d4169;
}

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.actions button {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.actions .delete-btn {
  color: #dc2626;
}

.category-row .actions .delete-btn {
  color: #dc2626;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  inset: 0;
  background: #ef4444;
  border-radius: 34px;
  transition: 0.2s;
}

.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  bottom: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}

.toggle-switch input:checked + .slider {
  background: #22c55e;
}

.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}

.animate-in {
  animation: 0.2s ease-out forwards;
}

.fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.zoom-in {
  from { transform: scale(0.97); }
  to { transform: scale(1); }
}
</style>
